import type {
  CachedFileInfo,
  CacheStats,
  DecryptedFile,
  FileCache,
  FileCacheConfig
} from "@9amhealth/shared";
import {
  base64ToBlob,
  blobToBase64,
  DEFAULT_MAX_AGE_MS,
  inferMimeTypeFromFilename
} from "@9amhealth/shared";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { StorageController } from "src/state/StorageBloc/StorageBloc";

const CACHE_DIR = "9amFileCache";
const METADATA_KEY = "fileMetadataCache";

interface CacheMetadataEntry {
  fileId: string;
  filename: string;
  mimeType: string;
  cachedAt: number;
}

type CacheMetadata = Record<string, CacheMetadataEntry | undefined>;

const getUserCacheDir = (userId: string): string => `${CACHE_DIR}/${userId}`;

const getFilePath = (userId: string, fileId: string): string =>
  `${getUserCacheDir(userId)}/${fileId}`;

const metadataCache = new Map<string, CacheMetadata>();
const userDirCreated = new Set<string>();

const loadMetadata = (userId: string): CacheMetadata => {
  const cached = metadataCache.get(userId);
  if (cached) {
    return cached;
  }

  const stored = StorageController.getItemParsed<CacheMetadata>(METADATA_KEY);
  const metadata = stored ?? {};
  metadataCache.set(userId, metadata);
  return metadata;
};

const saveMetadata = (userId: string, metadata: CacheMetadata): void => {
  metadataCache.set(userId, metadata);
  StorageController.setItem(METADATA_KEY, JSON.stringify(metadata));
};

export function createNativeFileCache(config: FileCacheConfig = {}): FileCache {
  const maxAgeMs = config.maxAgeMs ?? DEFAULT_MAX_AGE_MS;

  const reportError = (error: unknown, operation: string): void => {
    config.onError?.(error, { operation });
  };

  const ensureUserDir = async (userId: string): Promise<void> => {
    if (userDirCreated.has(userId)) {
      return;
    }

    try {
      await Filesystem.mkdir({
        path: getUserCacheDir(userId),
        directory: Directory.Cache,
        recursive: true
      });
      userDirCreated.add(userId);
    } catch {
      userDirCreated.add(userId);
    }
  };

  const getCachedFileInfo = async (
    fileId: string,
    userId: string
  ): Promise<CachedFileInfo | null> => {
    if (!userId) {
      return null;
    }

    try {
      const metadata = loadMetadata(userId);
      const entry = metadata[fileId];

      if (!entry) {
        return null;
      }

      if (Date.now() - entry.cachedAt > maxAgeMs) {
        void removeCachedFile(fileId, userId);
        return null;
      }

      const filePath = getFilePath(userId, fileId);
      const uriResult = await Filesystem.getUri({
        path: filePath,
        directory: Directory.Cache
      });

      const resolvedMimeType =
        entry.mimeType || inferMimeTypeFromFilename(entry.filename);

      return {
        filename: entry.filename,
        mimeType: resolvedMimeType,
        uri: uriResult.uri
      };
    } catch (error) {
      reportError(error, "getCachedFileInfo");
      return null;
    }
  };

  const getCachedFile = async (
    fileId: string,
    userId: string
  ): Promise<DecryptedFile | null> => {
    if (!userId) {
      return null;
    }

    try {
      const metadata = loadMetadata(userId);
      const entry = metadata[fileId];

      if (!entry) {
        return null;
      }

      if (Date.now() - entry.cachedAt > maxAgeMs) {
        void removeCachedFile(fileId, userId);
        return null;
      }

      const filePath = getFilePath(userId, fileId);

      const result = await Filesystem.readFile({
        path: filePath,
        directory: Directory.Cache
      });

      const base64Data = result.data as string;
      const resolvedMimeType =
        entry.mimeType || inferMimeTypeFromFilename(entry.filename);
      const blob = base64ToBlob(base64Data, resolvedMimeType);

      const uriResult = await Filesystem.getUri({
        path: filePath,
        directory: Directory.Cache
      });

      return {
        blob,
        filename: entry.filename,
        mimeType: resolvedMimeType,
        uri: uriResult.uri
      };
    } catch (error) {
      reportError(error, "getCachedFile");
      return null;
    }
  };

  const cacheFile = async (
    fileId: string,
    userId: string,
    blob: Blob,
    filename: string
  ): Promise<void> => {
    if (!userId) {
      return;
    }

    try {
      await ensureUserDir(userId);

      const base64Data = await blobToBase64(blob);

      await Filesystem.writeFile({
        path: getFilePath(userId, fileId),
        data: base64Data,
        directory: Directory.Cache
      });

      const resolvedMimeType = blob.type || inferMimeTypeFromFilename(filename);
      const metadata = loadMetadata(userId);

      metadata[fileId] = {
        fileId,
        filename,
        mimeType: resolvedMimeType,
        cachedAt: Date.now()
      };

      saveMetadata(userId, metadata);
    } catch (error) {
      reportError(error, "cacheFile");
    }
  };

  const removeCachedFile = async (
    fileId: string,
    userId: string
  ): Promise<void> => {
    if (!userId) {
      return;
    }

    try {
      await Filesystem.deleteFile({
        path: getFilePath(userId, fileId),
        directory: Directory.Cache
      });

      const metadata = loadMetadata(userId);
      metadata[fileId] = undefined;
      saveMetadata(userId, metadata);
    } catch {
      // Ignore errors on delete
    }
  };

  const clearUserFileCache = async (userId?: string): Promise<void> => {
    try {
      if (userId) {
        await Filesystem.rmdir({
          path: getUserCacheDir(userId),
          directory: Directory.Cache,
          recursive: true
        });
        metadataCache.delete(userId);
        userDirCreated.delete(userId);
        StorageController.removeItem(METADATA_KEY);
      } else {
        await Filesystem.rmdir({
          path: CACHE_DIR,
          directory: Directory.Cache,
          recursive: true
        });
        metadataCache.clear();
        userDirCreated.clear();
      }
    } catch {
      // Best effort cleanup
    }
  };

  const clearFileCache = async (): Promise<void> => {
    try {
      await Filesystem.rmdir({
        path: CACHE_DIR,
        directory: Directory.Cache,
        recursive: true
      });
      metadataCache.clear();
      userDirCreated.clear();
    } catch {
      // Best effort cleanup
    }
  };

  const getCacheStats = async (userId: string): Promise<CacheStats> => {
    if (!userId) {
      return { count: 0, totalSize: 0 };
    }

    const metadata = loadMetadata(userId);
    const count = Object.values(metadata).filter((e) => e !== undefined).length;

    return { count, totalSize: 0 };
  };

  return {
    getCachedFile,
    getCachedFileInfo,
    cacheFile,
    removeCachedFile,
    clearUserFileCache,
    clearFileCache,
    getCacheStats
  };
}
